import { Injectable } from '@angular/core';
import { CanDeactivate } from '@angular/router';
import { Observable } from 'rxjs';

/**
 * Interface that components must implement to use the CanDeactivate guard
 * Provides a contract for checking if navigation away is allowed
 */
export interface CanComponentDeactivate {
  /**
   * Called before navigation away from the component
   * Should return:
   * - true: Allow navigation immediately
   * - false: Block navigation
   * - Observable<boolean>: Async check (e.g., show modal, wait for user response)
   * - Promise<boolean>: Async check
   */
  canDeactivate: () => Observable<boolean> | Promise<boolean> | boolean;
}

/**
 * Production-Grade CanDeactivate Guard
 * 
 * Purpose:
 * - Prevents accidental data loss when navigating away from forms with unsaved changes
 * - Shows existing "Do you want to save changes?" modal
 * - Blocks navigation until user responds (Yes/No)
 * 
 * Features:
 * - Works with all Angular navigation (routerLink, router.navigate(), browser back/forward)
 * - Async-safe (waits for user response and save completion)
 * - No memory leaks (proper Observable cleanup)
 * - Type-safe interface contract
 * 
 * Usage:
 * 1. Component implements CanComponentDeactivate interface
 * 2. Component provides canDeactivate() method
 * 3. Add guard to route configuration
 * 
 * Note: This guard does NOT handle browser refresh/close events.
 * Use window.beforeunload in component for that scenario.
 */
@Injectable({
  providedIn: 'root'
})
export class CanDeactivateGuard implements CanDeactivate<CanComponentDeactivate> {
  
  /**
   * Called by Angular Router before navigating away
   * 
   * @param component The component being navigated away from
   * @returns Observable/Promise/boolean indicating if navigation is allowed
   */
  canDeactivate(
    component: CanComponentDeactivate
  ): Observable<boolean> | Promise<boolean> | boolean {
    
    // Delegate to component's canDeactivate method
    // Component will:
    // 1. Check if there are unsaved changes
    // 2. If yes, show "Do you want to save changes?" modal
    // 3. Return Observable that resolves when user responds
    // 4. Handle save logic if user clicks "Yes"
    // 5. Return true (allow navigation) or false (block navigation)
    
    return component.canDeactivate ? component.canDeactivate() : true;
  }
}
